import {
  awards,
  furtherHonours,
  accreditations,
  affiliations,
  partnerships,
} from "../data/profileData";
import ChapterMark from "../components/ChapterMark";
import { motion } from "framer-motion";
import { Award } from "lucide-react";
import { Reveal, staggerContainer, staggerItem } from "../components/Reveal";
import Seo from "../components/Seo";

export default function Awards() {
  return (
    <div data-testid="awards-page" className="bg-white">
      <Seo
        path="/awards"
        title="Honours & Awards"
        description="A legacy of excellence — 40+ honours, accreditations and airline partnerships earned by Dr. P. B. Boss and the Osaka Group across travel, tourism and overseas education."
      />
      {/* TITLE PAGE */}
      <section
        data-testid="awards-hero"
        className="pt-44 pb-24 lg:pt-52 lg:pb-32 bg-white relative"
      >
        <span className="hidden lg:block absolute left-7 top-1/2 -translate-y-1/2 v-label">
          P · B · Boss — Honours
        </span>
        <div className="max-w-[1640px] mx-auto px-8 sm:px-14 lg:px-20 text-center">
          <ChapterMark numeral="II" label="Honours & Recognitions" />
          <h1 className="mt-14 font-couture text-[clamp(3.5rem,9vw,8rem)] leading-[0.94] tracking-[-0.025em] text-[var(--ink)] reveal">
            A legacy of
            <br />
            <span className="font-couture-italic text-[var(--gold-deep)]">
              excellence, honoured.
            </span>
          </h1>
          <p className="mt-12 max-w-[42rem] mx-auto font-couture-italic text-xl lg:text-2xl text-[var(--ink-soft)] leading-[1.55]">
            A chronicle of recognitions bestowed across India, Bangkok, Dubai
            and Singapore — for leadership in travel, tourism, education and
            entrepreneurship.
          </p>
          <p className="mt-10 font-marcellus uppercase tracking-[0.42em] text-[0.66rem] text-[var(--gold-deep)]">
            {awards.length + furtherHonours.length} Honours · MMX — MMXXV
          </p>
        </div>
      </section>

      {/* CHRONOLOGICAL LIST — no cards */}
      <section data-testid="awards-grid" className="py-12 lg:py-20 bg-[var(--bg-soft)]">
        <div className="max-w-[1640px] mx-auto px-8 sm:px-14 lg:px-20">
          <ul className="border-t border-[var(--gold-line)]">
            {awards.map((a, i) => (
              <motion.li
                key={i}
                data-testid={`award-card-${i}`}
                variants={staggerItem}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-60px" }}
                className="group grid grid-cols-12 gap-5 lg:gap-8 py-9 lg:py-12 border-b border-[var(--gold-line)] hover:bg-white transition-colors items-start"
              >
                {/* Number */}
                <div className="col-span-2 lg:col-span-1">
                  <span className="numeral text-3xl lg:text-4xl text-[var(--gold-deep)]">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </div>

                {/* Date */}
                <div className="col-span-10 lg:col-span-2">
                  <p className="font-marcellus uppercase tracking-[0.32em] text-[0.7rem] text-[var(--ink-muted)]">
                    {a.date}
                  </p>
                </div>

                {/* Title + detail */}
                <div className="col-span-12 lg:col-span-6">
                  <h3 className="font-couture text-2xl lg:text-[2rem] text-[var(--ink)] leading-tight tracking-[-0.01em]">
                    {a.title}
                  </h3>
                  <p className="mt-3 font-couture-italic text-base lg:text-lg text-[var(--ink-soft)] leading-[1.55] max-w-[36rem]">
                    {a.detail}
                  </p>
                </div>

                {/* Image thumbnail (or gold seal when no photo exists) */}
                <div className="col-span-12 lg:col-span-3">
                  {a.image ? (
                    <div className="img-frame aspect-[4/3] overflow-hidden">
                      <img
                        src={a.image}
                        alt={a.title}
                        className="w-full h-full object-cover img-soft"
                        loading="lazy"
                      />
                    </div>
                  ) : (
                    <div className="img-frame aspect-[4/3] flex items-center justify-center bg-[var(--bg-soft)]">
                      <span className="flex flex-col items-center gap-2 text-[var(--gold-deep)]">
                        <Award size={30} strokeWidth={1} />
                        <span className="font-marcellus uppercase tracking-[0.3em] text-[0.55rem]">
                          Osaka Group
                        </span>
                      </span>
                    </div>
                  )}
                </div>
              </motion.li>
            ))}
          </ul>
        </div>
      </section>

      {/* FURTHER HONOURS — text chronicle (no images) */}
      <section
        data-testid="further-honours"
        className="py-28 lg:py-40 bg-white"
      >
        <div className="max-w-[1640px] mx-auto px-8 sm:px-14 lg:px-20">
          <Reveal className="mb-16 lg:mb-20 max-w-[44rem]">
            <p className="font-marcellus uppercase tracking-[0.5em] text-[0.66rem] text-[var(--gold-deep)] mb-6">
              The Wider Chronicle
            </p>
            <h2 className="font-couture text-4xl sm:text-5xl lg:text-6xl tracking-[-0.02em] leading-[1]">
              Further honours &{" "}
              <span className="font-couture-italic text-[var(--gold-deep)]">
                recognitions.
              </span>
            </h2>
          </Reveal>

          <motion.ul
            className="border-t border-[var(--gold-line)]"
            variants={staggerContainer(0.06)}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-60px" }}
          >
            {furtherHonours.map((a, i) => (
              <motion.li
                key={i}
                data-testid={`further-honour-${i}`}
                variants={staggerItem}
                className="group grid grid-cols-12 gap-5 lg:gap-8 py-8 lg:py-10 border-b border-[var(--gold-line)] hover:bg-[var(--bg-soft)] transition-colors items-center px-2 -mx-2"
              >
                <div className="col-span-12 lg:col-span-2">
                  <p className="font-marcellus uppercase tracking-[0.3em] text-[0.7rem] text-[var(--ink-muted)]">
                    {a.date}
                  </p>
                </div>
                <h3 className="col-span-12 lg:col-span-4 font-couture text-2xl lg:text-[1.7rem] text-[var(--ink)] leading-tight tracking-[-0.01em] group-hover:text-[var(--gold-deep)] transition-colors">
                  {a.title}
                </h3>
                <p
                  className={`col-span-12 ${
                    a.image ? "lg:col-span-4" : "lg:col-span-6"
                  } font-couture-italic text-base lg:text-lg text-[var(--ink-soft)] leading-[1.55]`}
                >
                  {a.detail}
                </p>
                {a.image && (
                  <div className="col-span-12 lg:col-span-2">
                    <div className="img-frame aspect-square overflow-hidden max-w-[160px] lg:max-w-none">
                      <img
                        src={a.image}
                        alt={a.title}
                        loading="lazy"
                        className="w-full h-full object-cover img-soft transition-transform duration-700 group-hover:scale-[1.04]"
                      />
                    </div>
                  </div>
                )}
              </motion.li>
            ))}
          </motion.ul>
        </div>
      </section>

      {/* ACCREDITATIONS & MEMBERSHIPS */}
      <section
        data-testid="accreditations"
        className="py-28 lg:py-40 bg-[var(--bg-soft)]"
      >
        <div className="max-w-[1640px] mx-auto px-8 sm:px-14 lg:px-20">
          <Reveal className="text-center mb-16 lg:mb-20">
            <p className="font-marcellus uppercase tracking-[0.5em] text-[0.66rem] text-[var(--gold-deep)] mb-6">
              Standards & Standing
            </p>
            <h2 className="font-couture text-4xl sm:text-5xl lg:text-6xl tracking-[-0.02em] leading-[1]">
              Accreditations &{" "}
              <span className="font-couture-italic text-[var(--gold-deep)]">
                memberships.
              </span>
            </h2>
          </Reveal>

          <div className="grid lg:grid-cols-2 gap-x-20 gap-y-16">
            {/* Accreditations */}
            <Reveal>
              <p className="font-marcellus uppercase tracking-[0.42em] text-[0.66rem] text-[var(--gold-deep)] mb-8 pb-5 border-b border-[var(--gold-line)]">
                Accreditations & Certifications
              </p>
              <motion.ul
                variants={staggerContainer(0.07)}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-60px" }}
              >
                {accreditations.map((c, i) => (
                  <motion.li
                    key={i}
                    data-testid={`accreditation-${i}`}
                    variants={staggerItem}
                    className="py-6 border-b border-[var(--gold-line-soft)]"
                  >
                    <h3 className="font-couture text-2xl text-[var(--ink)] leading-snug">
                      {c.name}
                    </h3>
                    <p className="mt-2 font-couture-italic text-base lg:text-lg text-[var(--ink-soft)] leading-[1.55]">
                      {c.detail}
                    </p>
                  </motion.li>
                ))}
              </motion.ul>
            </Reveal>

            {/* Affiliations */}
            <Reveal delay={0.12}>
              <p className="font-marcellus uppercase tracking-[0.42em] text-[0.66rem] text-[var(--gold-deep)] mb-8 pb-5 border-b border-[var(--gold-line)]">
                Industry Memberships
              </p>
              <motion.ul
                variants={staggerContainer(0.07)}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-60px" }}
              >
                {affiliations.map((m, i) => (
                  <motion.li
                    key={i}
                    data-testid={`affiliation-${i}`}
                    variants={staggerItem}
                    className="flex items-baseline gap-6 py-6 border-b border-[var(--gold-line-soft)]"
                  >
                    <span className="font-couture text-xl text-[var(--gold-deep)] w-44 shrink-0 leading-snug">
                      {m.name}
                    </span>
                    <span className="font-couture-italic text-base lg:text-lg text-[var(--ink-soft)] leading-[1.5]">
                      {m.detail}
                    </span>
                  </motion.li>
                ))}
              </motion.ul>
            </Reveal>
          </div>
        </div>
      </section>

      {/* AIRLINE & INDUSTRY PARTNERSHIPS */}
      <section
        data-testid="partnerships"
        className="py-28 lg:py-40 bg-white"
      >
        <div className="max-w-[1640px] mx-auto px-8 sm:px-14 lg:px-20">
          <Reveal className="mb-16 lg:mb-20 max-w-[44rem]">
            <p className="font-marcellus uppercase tracking-[0.5em] text-[0.66rem] text-[var(--gold-deep)] mb-6">
              Trusted By The Skies
            </p>
            <h2 className="font-couture text-4xl sm:text-5xl lg:text-6xl tracking-[-0.02em] leading-[1]">
              Airline &{" "}
              <span className="font-couture-italic text-[var(--gold-deep)]">
                industry partnerships.
              </span>
            </h2>
          </Reveal>

          <motion.div
            className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[var(--gold-line)] border border-[var(--gold-line)]"
            variants={staggerContainer(0.05)}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-60px" }}
          >
            {partnerships.map((p, i) => (
              <motion.div
                key={i}
                data-testid={`partnership-${i}`}
                variants={staggerItem}
                className="group bg-white hover:bg-[var(--bg-soft)] transition-colors"
              >
                <div className="aspect-[4/3] overflow-hidden bg-[var(--bg-soft)] border-b border-[var(--gold-line)]">
                  <img
                    src={p.image}
                    alt={`${p.airline} — ${p.note}`}
                    loading="lazy"
                    className="w-full h-full object-contain p-6 lg:p-8 transition-transform duration-700 group-hover:scale-[1.05]"
                  />
                </div>
                <div className="p-8 lg:p-10">
                  <h3 className="font-couture text-2xl lg:text-[1.7rem] text-[var(--ink)] leading-tight">
                    {p.airline}
                  </h3>
                  <p className="mt-3 font-marcellus uppercase tracking-[0.24em] text-[0.66rem] text-[var(--gold-deep)] leading-relaxed">
                    {p.note}
                  </p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>
    </div>
  );
}
